import { ArcElement, Plugin } from 'chart.js';

/**
 * Paints the canvas white before the chart is drawn.
 *
 * A Chart.js canvas is transparent, so whatever sits behind it shows through
 * the plot area — with the Material theme that is the card's tinted surface
 * rather than white. A CSS background on the container would fix the on-screen
 * appearance, but it is not part of the canvas: anything that rasterises the
 * chart (`toDataURL`, a copy-image, a print stylesheet) would still come out
 * transparent, which most viewers render as black. Filling the canvas itself
 * covers both cases.
 *
 * `destination-over` paints underneath what has already been drawn, so this is
 * safe wherever in the draw cycle it runs.
 */
export const whiteCanvasBackground: Plugin = {
  id: 'whiteCanvasBackground',
  beforeDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#fff';
    // chart.width/height are CSS pixels; the context is already scaled for the
    // device pixel ratio, so these are the right units here.
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};

/**
 * Draws "count (share%)" on two lines inside each pie slice.
 *
 * Chart.js has no built-in datalabels; the usual answer is
 * chartjs-plugin-datalabels, which is a whole dependency for two lines of
 * text. Arc elements already expose their own centre point, so this does the
 * job in a short afterDatasetsDraw instead.
 *
 * Numbers are grouped with the chart's own `locale` option — a first-class
 * Chart.js setting — so the wedges match however the rest of the page formats.
 */
export const pieSliceLabels: Plugin<'pie'> = {
  id: 'pieSliceLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const values = (chart.data.datasets[0]?.data ?? []) as number[];
    const total = values.reduce((sum, value) => sum + (value ?? 0), 0);
    if (!total) {
      return;
    }

    const format = new Intl.NumberFormat(chart.options.locale);

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#FFF';
    ctx.font = '600 16px Roboto, "Helvetica Neue", sans-serif';

    // getDatasetMeta types its elements as the generic base, which has no
    // geometry; on a pie they are always arcs.
    const arcs = chart.getDatasetMeta(0).data as unknown as ArcElement[];

    arcs.forEach((arc, index) => {
      const value = values[index] ?? 0;
      const share = value / total;
      // Too thin a wedge can't hold two lines; the tooltip still reports it.
      if (share < 0.06) {
        return;
      }
      // false = the arc's current position, so a label rides its wedge while
      // the chart animates in rather than floating at the final spot.
      const { x, y } = arc.getCenterPoint(false);
      /* ctx.fillText(format.format(value), x, y - 8); */
      ctx.fillText(`${Math.round(share * 100)}%`, x+8, y - 4);
    });

    ctx.restore();
  }
};
